import React, { FC, type RefObject, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ATTR_NAMES_LEAD } from '~/src/models/constants/attr-names-lead.constants';
import { selectSuggestedOrganization } from '~/src/redux/lead/lead.selectors';
import { selectCurrentOrganizationIsByPrivileged } from '~/src/redux/user/user.selectors';
import { PrivilegesEnum } from '~/src/models/enums/privileges.enum';
import { onSendOrganizationInfoAnalytics } from '~/src/components/blocks/referral-products/organization-info-ref/organization-info.analytics';
import checkFieldsSuccessFillForAnalytics from '~/src/helpers/analytics-ref/analytics-ref.helper';
import {
    INITIAL_CREATE_LEAD_DATA_INN,
    INITIAL_CREATE_LEAD_DATA_OGRN,
} from '~/src/components/blocks/referral-products/organization-info-ref/organization-info.constants';
import type { WebAnalyticsEventCategoryEnum } from '~/src/models/enums/web-analytics.enum';
import type { UseRefType } from './organization-info-sub-data-inputs.types';
import type { RootState } from '~/src/redux/store';
import * as Styled from './organization-info-sub-data-inputs.styles';

export interface OrganizationInfoSubDataInputsProps {
    innErrorRef: RefObject<HTMLDivElement>;
    ogrnErrorRef: RefObject<HTMLDivElement>;
    disabledRegInputs?: boolean;
    eventCategory: WebAnalyticsEventCategoryEnum;
}

export const OrganizationInfoSubDataInputs: FC<OrganizationInfoSubDataInputsProps> = ({
    disabledRegInputs,
    innErrorRef,
    ogrnErrorRef,
    eventCategory,
}) => {
    const { inn, ogrn } = useSelector(selectSuggestedOrganization);
    const innSuccessIconForAnalyticsRef = useRef<UseRefType>(INITIAL_CREATE_LEAD_DATA_INN);
    const innErrorIconForAnalyticsRef = useRef<UseRefType>(INITIAL_CREATE_LEAD_DATA_INN);
    const ogrnSuccessIconForAnalyticsRef = useRef<UseRefType>(INITIAL_CREATE_LEAD_DATA_OGRN);
    const ogrnErrorIconForAnalyticsRef = useRef<UseRefType>(INITIAL_CREATE_LEAD_DATA_OGRN);

    const currentOrganizationIsPrivileged = useSelector(
        (state: RootState) => (args: PrivilegesEnum) => selectCurrentOrganizationIsByPrivileged(state, args),
    );

    return (
        <Styled.OrganizationInfoSubDataInputsWrapper disabledRegInputs={disabledRegInputs}>
            <Styled.RegContainer>
                <Styled.RegNumberInputWrap
                    name={ATTR_NAMES_LEAD.inn}
                    label={'ИНН'}
                    externalValue={inn}
                    regNumbersType={ATTR_NAMES_LEAD.inn}
                    errorText={'Некорректное значение ИНН'}
                    placeholder={'10-12 цифр'}
                    required={!currentOrganizationIsPrivileged(PrivilegesEnum.CREATE_LEAD_WITH_INCOMPLETE_DATA)}
                    disabled={!disabledRegInputs}
                    inputErrorRef={innErrorRef}
                    analyticsref={checkFieldsSuccessFillForAnalytics.bind({
                        filedName: ATTR_NAMES_LEAD.inn,
                        dataSuccessRef: innSuccessIconForAnalyticsRef,
                        sendAnalytics: () => onSendOrganizationInfoAnalytics('inn', eventCategory),
                    })}
                    analyticsreferror={checkFieldsSuccessFillForAnalytics.bind({
                        filedName: ATTR_NAMES_LEAD.inn,
                        dataSuccessRef: innErrorIconForAnalyticsRef,
                        sendAnalytics: () =>
                            onSendOrganizationInfoAnalytics('inn', eventCategory, 'Некорректное значение ИНН'),
                    })}
                />
                <Styled.RegNumberInputWrap
                    name={ATTR_NAMES_LEAD.ogrn}
                    label={'ОГРН'}
                    externalValue={ogrn}
                    regNumbersType={ATTR_NAMES_LEAD.ogrn}
                    errorText={'Некорректное значение ОГРН'}
                    placeholder={'13-15 цифр'}
                    required={!currentOrganizationIsPrivileged(PrivilegesEnum.CREATE_LEAD_WITH_INCOMPLETE_DATA)}
                    disabled={!disabledRegInputs}
                    inputErrorRef={ogrnErrorRef}
                    analyticsref={checkFieldsSuccessFillForAnalytics.bind({
                        filedName: ATTR_NAMES_LEAD.ogrn,
                        dataSuccessRef: ogrnSuccessIconForAnalyticsRef,
                        sendAnalytics: () => onSendOrganizationInfoAnalytics('ogrn', eventCategory),
                    })}
                    analyticsreferror={checkFieldsSuccessFillForAnalytics.bind({
                        filedName: ATTR_NAMES_LEAD.ogrn,
                        dataSuccessRef: ogrnErrorIconForAnalyticsRef,
                        sendAnalytics: () =>
                            onSendOrganizationInfoAnalytics('ogrn', eventCategory, 'Некорректное значение ОГРН'),
                    })}
                />
            </Styled.RegContainer>
        </Styled.OrganizationInfoSubDataInputsWrapper>
    );
};
